export interface Volume {
  number: string
  title: string
  subtitle: string
  problem: string
  bullets: string[]
  sessions?: string
  result: string
  popular?: boolean
}

export const volumes: Volume[] = [
  {
    number: 'Volume 1',
    title: 'Boxing Foundations & Core Mechanics',
    subtitle: 'Build the technical base everything else depends on',
    popular: true,
    problem:
      'This volume gives you a clear progression for the essential mechanics every boxer needs before more speed, complexity or tactical pressure is added.',
    bullets: [
      'Boxing stance',
      'Guard',
      'Balance',
      'Body control',
      'Jab mechanics',
      'Cross mechanics',
      'Lead hook',
      'Rear hook',
      'Uppercuts',
      'Punch recovery',
      'Forward movement',
      'Backward movement',
      'Lateral movement',
      'Distance awareness',
      'Fundamental combinations',
      'Coaching cues',
      'Common mistakes',
      'Corrections',
      'Regressions',
      'Progressions',
      'Foundation assessments',
    ],
    sessions: '25 complete sessions',
    result:
      'Your athletes build cleaner fundamentals, and you know exactly what to correct before progressing them.',
  },
  {
    number: 'Volume 2',
    title: 'Footwork, Defense & Counterpunching',
    subtitle: 'Move, defend and respond without losing position',
    problem:
      'Defense should not finish with the athlete out of balance, out of range or unable to respond. This volume connects movement, defensive mechanics and counterpunching into one progressive coaching system.',
    bullets: [
      'Pivots',
      'Angles',
      'Exits',
      'Ring positioning',
      'Movement under pressure',
      'Slips',
      'Rolls',
      'Blocks',
      'Parries',
      'Catches',
      'Pullbacks',
      'Defensive recovery',
      'Countering the jab',
      'Countering straight punches',
      'Countering hooks',
      'Defense-to-counter transitions',
      'Decision-making',
      'Diagnostic coaching',
    ],
    sessions: '25 complete sessions',
    result:
      'Your athletes stop treating defense as an isolated movement and start defending into useful positions.',
  },
  {
    number: 'Volume 3',
    title: 'Combinations, Pad Work & Partner Drills',
    subtitle: 'Turn isolated skills into connected boxing',
    problem:
      'Long combinations and flashy pad work do not automatically produce better boxers. The goal is purpose, range, rhythm, position and decision-making.',
    bullets: [
      'Two-punch combinations',
      'Three-punch combinations',
      'Four-punch control',
      'Rhythm development',
      'Body-to-head combinations',
      'Range changes',
      'Combination + footwork',
      'Combination + angle',
      'Combination + defense',
      'Combination + exit',
      'Pad-holder fundamentals',
      'Rhythm pad work',
      'Defensive pad work',
      'Counterpunching on pads',
      'Accuracy',
      'Controlled power',
      'Partner range drills',
      'Offense/defense roles',
      'Reaction',
      'Timing',
      'Controlled pressure',
      'Limited decision-making',
    ],
    sessions: '25 complete sessions',
    result:
      'Your combination, pad and partner work becomes more realistic, purposeful and transferable to actual boxing.',
  },
  {
    number: 'Volume 4',
    title: 'Bag Work, Conditioning & Fight-Specific Training',
    subtitle: 'Build work capacity without destroying technique',
    problem:
      'The heavy bag does not correct your athlete. And conditioning is not useful if the athlete’s boxing falls apart as soon as fatigue appears.',
    bullets: [
      'Heavy-bag range',
      'Jab development',
      'Straight-punch development',
      'Hooks',
      'Uppercuts',
      'Body-head work',
      'Rhythm',
      'Movement and re-entry',
      'Controlled power',
      'Accuracy',
      'Technical speed',
      'Double-end bag',
      'Speed bag',
      'Slip bag',
      'Specialty bag work',
      'Pacing',
      'Technical density',
      'Short-burst conditioning',
      'Repeated effort',
      'Round-based conditioning',
      'Recovery between rounds',
      'Boxing-specific circuits',
      'Technique under fatigue',
      'Decision-making under fatigue',
    ],
    sessions: '25 complete sessions',
    result:
      'Your conditioning supports boxing quality instead of teaching athletes how to become technically sloppy when tired.',
  },
  {
    number: 'Volume 5',
    title: 'Tactics, Ringcraft & Boxer Development',
    subtitle: 'Teach athletes how to control the exchange',
    problem:
      'Technique tells the athlete how to perform a skill. Tactics tells them where to be, when to enter, when to exit, what range they want, how to respond to pressure and what to change next round.',
    bullets: [
      'Range ownership',
      'Entry and exit',
      'Center control',
      'Ropes',
      'Corners',
      'Ring geography',
      'Lead-hand control',
      'Initiative',
      'Space management',
      'Angle ownership',
      'Outside boxing',
      'Pressure fighting',
      'Counterboxing',
      'Mid-range control',
      'Inside boxing',
      'Hybrid styles',
      'Style matchups',
      'Opponent analysis',
      'Pattern recognition',
      'Fight-plan development',
      'Round strategy',
      'Tactical adjustments',
      'Ring generalship',
    ],
    sessions: '25 complete sessions',
    result:
      'Your athletes begin making better boxing decisions instead of simply performing techniques.',
  },
  {
    number: 'Volume 6',
    title: 'Complete Session Vault & Master Coaching Reference',
    subtitle: 'Real-world session adaptations plus the reference that connects the entire system',
    problem:
      'A session needs to work in the real gym: no heavy bag, no pads, one athlete, twelve athletes, mixed levels, limited space, a shortened session, or the day your original plan stops working.',
    bullets: [
      '25 complete core sessions',
      'Beginner development',
      'Intermediate progressions',
      'Advanced variations',
      'Reaction training',
      'Decision-making',
      'Tactical constraints',
      'Fight-specific application',
      'No-bag alternatives',
      'No-pad alternatives',
      'No-ring alternatives',
      'Limited-space adaptations',
      'Small-group systems',
      'Large-group systems',
      'Mixed-level coaching',
      'Short-session formats',
      'Emergency structures',
      'No-prep systems',
      'Master coaching framework',
      'Technical vs tactical errors',
      'Complexity-based errors',
      'Fatigue-based errors',
      'Symptom vs cause diagnosis',
      'Foundation corrections',
      'Movement diagnostics',
      'Defense diagnostics',
      'Counterpunching diagnostics',
      'Combination diagnostics',
      'Pad-work diagnostics',
      'Partner-work diagnostics',
      'Bag-work diagnostics',
      'Conditioning diagnostics',
      'Tactical diagnostics',
      'Advanced coaching case studies',
      'Error-to-correction reference',
      'Master progression system',
      'Coach cue library',
      'Expanded session material',
      'Final coaching principles',
    ],
    result:
      'You always have a professional solution — even when conditions are not ideal — and you coach from a clear Observe → Identify → Cue → Correct → Retest → Progress system.',
  },
]
